import { evaluateQaTenantAccess, resolveQaTenantKey } from './tenantAccess';
import { runHealthChecks, healthRowsPending } from './healthChecks';
import { MODULE_FILTER_ID, moduleRowsPending, runModuleChecks } from './moduleChecks';
import { createBrowserQaClient } from './timecardScenarios/browserClient';
import { qaTimecardConfig, timecardScenarioCatalog } from './timecardScenarios/scenarioCatalog';
import { runScenario } from './timecardScenarios/runners';
import { mergeTenantDefaults } from './timecardScenarios/sandboxIds';
import { prepareRowsPending, prepareSandbox } from './timecardScenarios/prepareSandbox';
import type { QaCheckRow, QaTimecardDefaults, TimecardScenario } from './timecardScenarios/types';

/** Runs health checks, module smokes, then the shared timecard scenario catalog. */

export type QaSuiteMode = 'all' | 'health' | 'modules' | string;

export const TIMECARD_GROUP_FILTERS = [
  'sync',
  'match',
  'unscheduled',
  'open-punch',
  'paid',
  'break',
  'location',
  'approval',
  'discover',
] as const;

export function scenarioGroup(scenario: TimecardScenario): string {
  const tags = scenario.tags || [];
  return TIMECARD_GROUP_FILTERS.find((t) => tags.includes(t)) || tags[0] || scenario.type;
}

export function isTimecardFilter(mode: QaSuiteMode): boolean {
  return TIMECARD_GROUP_FILTERS.includes(mode as (typeof TIMECARD_GROUP_FILTERS)[number]);
}

export function suiteScenarios(): TimecardScenario[] {
  return (timecardScenarioCatalog as TimecardScenario[])
    .filter((s: TimecardScenario) => s.runInSuite !== false)
    .sort((a: TimecardScenario, b: TimecardScenario) => a.id.localeCompare(b.id));
}

export function scenarioRowsPending(filterGroup?: string): QaCheckRow[] {
  return suiteScenarios()
    .filter((s) => !filterGroup || filterGroup === 'all' || scenarioGroup(s) === filterGroup)
    .map((s) => ({
      id: s.id,
      title: s.title,
      group: scenarioGroup(s),
      status: 'pending' as const,
      message: '',
    }));
}

export function buildInitialRows(mode: QaSuiteMode, defaults?: QaTimecardDefaults): QaCheckRow[] {
  const prepare = prepareRowsPending(defaults || qaTimecardConfig.defaults);
  const health = healthRowsPending();
  const modules = moduleRowsPending(mode === MODULE_FILTER_ID ? MODULE_FILTER_ID : mode);
  if (mode === 'health') return health;
  if (mode === MODULE_FILTER_ID) return modules;
  if (mode === 'all') return [...prepare, ...health, ...moduleRowsPending('all'), ...scenarioRowsPending()];
  if (isTimecardFilter(mode)) return [...prepare, ...health, ...scenarioRowsPending(mode)];
  return moduleRowsPending(mode);
}

function skipTimecards(scenarios: TimecardScenario[], onRow: (row: QaCheckRow) => void, message: string) {
  for (const scenario of scenarios) {
    onRow({
      id: scenario.id,
      title: scenario.title,
      group: scenarioGroup(scenario),
      status: 'skipped',
      message,
    });
  }
}

export async function runQaSuite(
  mode: QaSuiteMode,
  onRow: (row: QaCheckRow) => void
): Promise<void> {
  const access = evaluateQaTenantAccess();
  if (!access.allowed) {
    throw new Error(access.reason);
  }

  const defaults = mergeTenantDefaults(
    qaTimecardConfig,
    resolveQaTenantKey(access.tenantCode, access.tenantName)
  );
  const runTimecards = mode === 'all' || isTimecardFilter(mode);
  const runModules = mode === 'all' || mode === MODULE_FILTER_ID;
  const scenarios = suiteScenarios().filter(
    (s) => mode === 'all' || scenarioGroup(s) === mode
  );

  if (mode === 'health') {
    await runHealthChecks(onRow);
    return;
  }

  if (mode === MODULE_FILTER_ID) {
    await runModuleChecks(defaults, onRow, MODULE_FILTER_ID);
    return;
  }

  if (!runTimecards && !runModules) {
    await runModuleChecks(defaults, onRow, mode);
    return;
  }

  const client = createBrowserQaClient();
  const prep = await prepareSandbox(client, defaults, scenarios, onRow, qaTimecardConfig);
  if (!prep.seedOk || !prep.cleanupOk) {
    await runHealthChecks(onRow);
    if (runModules) {
      await runModuleChecks(defaults, onRow, 'all');
    }
    skipTimecards(scenarios, onRow, 'Skipped because seed or cleanup did not succeed');
    return;
  }

  await runHealthChecks(onRow);
  if (runModules) {
    await runModuleChecks(defaults, onRow, 'all');
  }

  const ctx = { client, config: qaTimecardConfig, defaults };
  for (const scenario of scenarios) {
    const group = scenarioGroup(scenario);
    onRow({
      id: scenario.id,
      title: scenario.title,
      group,
      status: 'running',
      message: '',
    });
    try {
      const result = await runScenario(ctx, scenario);
      onRow({
        id: scenario.id,
        title: scenario.title,
        group,
        status: result.status,
        message: result.message,
      });
    } catch (err: unknown) {
      onRow({
        id: scenario.id,
        title: scenario.title,
        group,
        status: 'failed',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }
}
